import { z } from 'zod';

// ─── Nexus Token ────────────────────────────────────────────────────────────
// Zod contract for the Nexus-signed JWT payload.
// Firebase handles identity (login). At token exchange the API mints a
// Nexus JWT containing the user's access_ids. All downstream services
// verify this token — never the raw Firebase token.

/** Default JWT lifetime in seconds; overridden at runtime by `AUTH_TOKEN_TTL_SECONDS` when set. */
export const NEXUS_JWT_TTL_SECONDS_DEFAULT = 3600 as const;

export const NexusTokenPayloadSchema = z.object({
    /** Subject — Firebase UID */
    sub: z.string().min(1),
    /** Access-group IDs the user belongs to (each maps to a tenant) */
    acs: z.array(z.string().min(1)),
    /** User email (from Firebase) */
    email: z.string().email(),
    /** Issued-at timestamp (epoch seconds) */
    iat: z.number(),
    /** Expiration timestamp (epoch seconds) */
    exp: z.number(),
    /** Issuer — always "nexus" for Nexus-signed tokens */
    iss: z.literal('nexus'),
    /** JWT ID — unique per token, useful for revocation */
    jti: z.string().min(1),
});

export type NexusTokenPayload = z.infer<typeof NexusTokenPayloadSchema>;

// ─── Firebase Identity ──────────────────────────────────────────────────────
// Minimal shape extracted from a verified Firebase ID token.
// Only used at the /auth/exchange boundary.

export const FirebaseIdentitySchema = z.object({
    /** Firebase UID */
    uid: z.string().min(1),
    /** Email address */
    email: z.string().email().optional(),
    /** Whether Firebase has verified the email */
    emailVerified: z.boolean().optional(),
    /** Auth provider from Firebase token (e.g. "google.com", "password") */
    signInProvider: z.string().optional(),
    /** Google profile photo URL (only present for google.com provider) */
    photoUrl: z.string().url().optional(),
});

export type FirebaseIdentity = z.infer<typeof FirebaseIdentitySchema>;
